import React, { useEffect, useState } from "react";
import { api, openAuthedFile, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FolderOpen, Search, Upload, FileText, Trash2, Loader2, Download, Clock, Settings, Plus, StickyNote, Import } from "lucide-react";
import { toast } from "sonner";

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const EVENT_COLORS = {
  creation: "bg-blue-100 text-blue-800", reinscription: "bg-purple-100 text-purple-800",
  document: "bg-green-100 text-green-800", document_removed: "bg-red-100 text-red-700",
  import: "bg-orange-100 text-orange-800", note: "bg-slate-100 text-slate-700",
};

export default function DossiersElevesPage() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin", "director"].includes(user?.role);
  const canManage = ["scolarite", "admin", "super_admin", "director"].includes(user?.role);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  // Upload
  const [upOpen, setUpOpen] = useState(false);
  const [upForm, setUpForm] = useState({ doc_name: "", doc_type_id: "", file: null, notes: "" });
  const [saving, setSaving] = useState(false);
  // Import inscription en ligne
  const [importRef, setImportRef] = useState("");
  // Note
  const [note, setNote] = useState("");
  // Config admin
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgForm, setCfgForm] = useState({ name: "", required: false });

  const loadDocTypes = () => api.get("/dossiers/required-documents").then(r => setDocTypes(r.data)).catch(() => {});
  useEffect(() => { loadDocTypes(); }, []);

  const search = async () => {
    setLoading(true);
    setDossier(null);
    try {
      const { data } = await api.get("/dossiers/search", { params: { q } });
      setResults(data);
      if (data.length === 0) toast.info("Aucun élève ou dossier trouvé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de recherche"); }
    finally { setLoading(false); }
  };

  const openDossier = async (studentId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dossiers/student/${studentId}`);
      setDossier(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const uploadDoc = async () => {
    if (!upForm.file) return toast.error("Choisissez un fichier");
    if (!upForm.doc_name.trim()) return toast.error("Nommez le document");
    setSaving(true);
    try {
      const b64 = await fileToBase64(upForm.file);
      await api.post(`/dossiers/student/${dossier.student_id}/documents`, {
        doc_name: upForm.doc_name, doc_type_id: upForm.doc_type_id || null,
        file_data_b64: b64, notes: upForm.notes,
      });
      toast.success("Document ajouté au dossier");
      setUpOpen(false);
      setUpForm({ doc_name: "", doc_type_id: "", file: null, notes: "" });
      openDossier(dossier.student_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSaving(false); }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Retirer "${d.doc_name}" du dossier ?`)) return;
    try {
      await api.delete(`/dossiers/documents/${d.id}`);
      toast.success("Document retiré");
      openDossier(dossier.student_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const importEnrollment = async () => {
    if (!importRef.trim()) return toast.error("Saisissez la référence d'inscription en ligne");
    try {
      const { data } = await api.post(`/dossiers/student/${dossier.student_id}/import-enrollment/${importRef.trim()}`);
      toast.success(`${data.imported} document(s) importé(s)`);
      setImportRef("");
      openDossier(dossier.student_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'import"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/dossiers/student/${dossier.student_id}/history`, { detail: note });
      toast.success("Note ajoutée à l'historique");
      setNote("");
      openDossier(dossier.student_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const addDocType = async () => {
    if (!cfgForm.name.trim()) return toast.error("Nom obligatoire");
    try {
      await api.post("/dossiers/required-documents", cfgForm);
      toast.success("Type de document ajouté");
      setCfgForm({ name: "", required: false });
      loadDocTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeDocType = async (dt) => {
    if (!window.confirm(`Désactiver "${dt.name}" ?`)) return;
    try { await api.delete(`/dossiers/required-documents/${dt.id}`); loadDocTypes(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="dossiers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><FolderOpen className="h-3 w-3" /><span>DOSSIERS ÉLÈVES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Dossiers des élèves</h1>
          <p className="text-sm text-slate-500 mt-1">Chaque élève a un dossier unique (n° DOS-AA-XXXXX) qui garde documents et historique de tout son parcours à l&apos;école.</p>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => setCfgOpen(true)} className="gap-1 border-[#1E3A8A] text-[#1E3A8A]" data-testid="doc-config-btn">
            <Settings className="h-4 w-4" />Documents requis (config)
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
               placeholder="N° de dossier (DOS-26-00001), matricule ou nom..." data-testid="dossier-search-input" />
        <Button onClick={search} disabled={loading} className="bg-[#1E3A8A] text-white gap-1" data-testid="dossier-search-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Rechercher
        </Button>
      </div>

      {/* Résultats de recherche */}
      {!dossier && results.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">N° Dossier</th>
                <th className="px-4 py-3 text-left">Élève</th>
                <th className="px-4 py-3 text-left">Matricule</th>
                <th className="px-4 py-3 text-left">Classe</th>
                <th className="px-4 py-3 text-center">Documents</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => (
                <tr key={r.student_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dossier-result-${r.student_id}`}>
                  <td className="px-4 py-2 font-mono text-xs font-bold text-[#1E3A8A]">{r.dossier_number || "— (sera créé)"}</td>
                  <td className="px-4 py-2 font-medium">{r.student?.first_name} {r.student?.last_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.student?.matricule}</td>
                  <td className="px-4 py-2">{r.student?.class_name}</td>
                  <td className="px-4 py-2 text-center">{(r.documents || []).length}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" onClick={() => openDossier(r.student_id)} className="bg-[#1E3A8A] text-white text-xs gap-1" data-testid={`open-dossier-${r.student_id}`}>
                      <FolderOpen className="h-3 w-3" />Consulter
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Vue dossier */}
      {dossier && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-wrap items-start justify-between gap-4" data-testid="dossier-detail">
            <div>
              <div className="font-mono font-extrabold text-2xl text-[#1E3A8A]" data-testid="dossier-number">{dossier.dossier_number}</div>
              <div className="font-heading font-bold text-xl text-[#0F172A] mt-1">{dossier.student?.first_name} {dossier.student?.last_name}</div>
              <div className="text-sm text-slate-600">Matricule : <span className="font-mono">{dossier.student?.matricule}</span> · {dossier.student?.class_name} · {dossier.student?.academic_year || ""}</div>
              <div className="text-xs text-slate-400 mt-1">Dossier ouvert le {fmtDate(dossier.created_at)}</div>
            </div>
            <Button variant="outline" onClick={() => setDossier(null)} className="text-xs">← Retour aux résultats</Button>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="font-heading font-bold text-[#1E3A8A]">Documents du dossier ({(dossier.documents || []).length})</span>
              {canManage && (
                <div className="flex gap-2 flex-wrap">
                  <div className="flex gap-1">
                    <Input value={importRef} onChange={e => setImportRef(e.target.value)} placeholder="Réf. inscription en ligne" className="h-8 w-44 text-xs" data-testid="import-ref-input" />
                    <Button size="sm" variant="outline" onClick={importEnrollment} className="text-xs gap-1 h-8" data-testid="import-enrollment-btn"><Import className="h-3 w-3" />Importer</Button>
                  </div>
                  <Button size="sm" onClick={() => setUpOpen(true)} className="bg-[#166534] text-white text-xs gap-1 h-8" data-testid="add-doc-btn"><Upload className="h-3 w-3" />Ajouter un document</Button>
                </div>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Document</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">Ajouté par</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(dossier.documents || []).map(d => (
                  <tr key={d.id} className="border-t border-slate-100" data-testid={`doc-row-${d.id}`}>
                    <td className="px-4 py-2 font-medium"><FileText className="h-3 w-3 inline mr-1 text-slate-400" />{d.doc_name}</td>
                    <td className="px-4 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${d.source === "parent" ? "bg-pink-100 text-pink-700" : "bg-cyan-100 text-cyan-700"}`}>{d.source === "parent" ? "Parent" : "Scolarité"}</span></td>
                    <td className="px-4 py-2 text-xs text-slate-500">{d.uploaded_by}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{fmtDate(d.uploaded_at)}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openAuthedFile(`/dossiers/documents/${d.id}/file`).catch(() => toast.error("Fichier indisponible"))} className="text-xs gap-1 h-7" data-testid={`download-doc-${d.id}`}><Download className="h-3 w-3" />Voir</Button>
                        {canManage && <Button size="sm" variant="outline" onClick={() => removeDoc(d)} className="text-xs h-7 border-red-300 text-red-600" data-testid={`delete-doc-${d.id}`}><Trash2 className="h-3 w-3" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {(dossier.documents || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucun document dans ce dossier</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Historique */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="font-heading font-bold text-[#1E3A8A] mb-3 flex items-center gap-2"><Clock className="h-4 w-4" />Historique du cycle de vie</div>
            {canManage && (
              <div className="flex gap-2 mb-4">
                <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Ajouter une note interne à l'historique..." className="text-sm" data-testid="history-note-input" />
                <Button size="sm" variant="outline" onClick={addNote} className="gap-1" data-testid="history-note-btn"><StickyNote className="h-3 w-3" />Noter</Button>
              </div>
            )}
            <div className="space-y-3">
              {(dossier.timeline || []).slice().reverse().map(ev => (
                <div key={ev.id} className="flex gap-3 items-start" data-testid={`timeline-${ev.id}`}>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0 ${EVENT_COLORS[ev.event] || "bg-slate-100 text-slate-700"}`}>
                    {({ creation: "Ouverture", reinscription: "Réinscription", document: "Document", document_removed: "Retrait", import: "Import", note: "Note" })[ev.event] || ev.event}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm text-slate-800">{ev.detail}</div>
                    <div className="text-[11px] text-slate-400">{fmtDate(ev.at)} · {ev.actor}</div>
                  </div>
                </div>
              ))}
              {(dossier.timeline || []).length === 0 && <div className="text-sm text-slate-400">Aucun événement</div>}
            </div>
          </div>
        </div>
      )}

      {/* Dialog upload */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent data-testid="upload-doc-dialog">
          <DialogHeader><DialogTitle>Ajouter un document au dossier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type de document</Label>
              <Select value={upForm.doc_type_id} onValueChange={(v) => {
                const dt = docTypes.find(x => x.id === v);
                setUpForm({ ...upForm, doc_type_id: v, doc_name: upForm.doc_name || dt?.name || "" });
              }}>
                <SelectTrigger data-testid="up-doc-type"><SelectValue placeholder="Choisir (optionnel)" /></SelectTrigger>
                <SelectContent>
                  {docTypes.map(dt => <SelectItem key={dt.id} value={dt.id}>{dt.name}{dt.required ? " *" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nom du document *</Label>
              <Input value={upForm.doc_name} onChange={e => setUpForm({ ...upForm, doc_name: e.target.value })} placeholder="Ex : Extrait de naissance" data-testid="up-doc-name" />
            </div>
            <div>
              <Label>Fichier * (PDF ou image, max 8 Mo)</Label>
              <Input type="file" accept=".pdf,image/*" onChange={e => setUpForm({ ...upForm, file: e.target.files?.[0] || null })} data-testid="up-doc-file" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={upForm.notes} onChange={e => setUpForm({ ...upForm, notes: e.target.value })} rows={2} data-testid="up-doc-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpOpen(false)}>Annuler</Button>
            <Button onClick={uploadDoc} disabled={saving} className="bg-[#166534] text-white gap-1" data-testid="up-doc-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog config admin */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent data-testid="doc-config-dialog">
          <DialogHeader><DialogTitle>Documents requis à l&apos;inscription (config Admin)</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {docTypes.map(dt => (
              <div key={dt.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`doctype-${dt.id}`}>
                <div className="text-sm">{dt.name} {dt.required && <span className="text-[10px] font-bold text-red-600 ml-1">OBLIGATOIRE</span>}</div>
                <Button size="sm" variant="outline" onClick={() => removeDocType(dt)} className="h-7 border-red-300 text-red-600 text-xs" data-testid={`del-doctype-${dt.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <Input value={cfgForm.name} onChange={e => setCfgForm({ ...cfgForm, name: e.target.value })} placeholder="Nouveau type de document..." data-testid="new-doctype-name" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={cfgForm.required} onChange={e => setCfgForm({ ...cfgForm, required: e.target.checked })} className="rounded" data-testid="new-doctype-required" />
              Obligatoire lors de l&apos;inscription en ligne
            </label>
            <Button onClick={addDocType} className="bg-[#1E3A8A] text-white gap-1 w-full" data-testid="new-doctype-submit"><Plus className="h-4 w-4" />Ajouter</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
